/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { useState, useEffect } from "react"
import { SectionGrid } from "frontend/src/components/Layout"
import { LogoColorable } from "frontend/src/components/LogoColorable"

// evaluate if a color is light or dark, put correct logo color as return value
function lightOrDark(color) {
  // bring hex value to six characters if necessary, remove hash character
  color = +("0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&"))
  // extract red, green, and blue values from hex code
  const r = color >> 16
  const g = (color >> 8) & 255
  const b = color & 255

  // calculate a mesaurement for brightness from rgb values
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b))
  if (hsp > 127.5) {
    return "black" // background color is light
  }
  return "white" // backgroundColor is dark
}

// desktop component for press logos
function PressLogosDesktop({ introTitle, backgroundColor, fontColor, pressLogos }) {
  // shown logos
  const [logos, setLogos] = useState(pressLogos.slice(0, 4))
  // componentDidMount
  useEffect(() => {
    // function to determine logos to show
    const subsectionOfLogos = () => {
      try {
        let max = window.innerWidth
        max = max > 1300 ? 1300 : max
        let maxLogos
        if (max >= 1120) {
          maxLogos = 4
        } else if (max >= 850) {
          maxLogos = 3
        } else {
          maxLogos = 2
        }
        const logoSelection = pressLogos.slice(0, maxLogos)
        setLogos(logoSelection)
      } catch (error) {
        setLogos(pressLogos.slice(0, 4))
      }
    }
    //
    try {
      window.addEventListener("load", subsectionOfLogos)
      window.addEventListener("resize", subsectionOfLogos)
    } catch (error) {
      console.error(error)
    }
  }, [pressLogos])
  // logo color is determined from brightness of backgroundColor
  const logoColor = lightOrDark(backgroundColor)
  return (
    <SectionGrid
      sx={{
        display: ["none", "none", "grid"],
        backgroundColor: "inherit",
        color: fontColor,
      }}
    >
      <div
        sx={{
          gridColumn: "2 / span 3",
          color: fontColor,
          backgroundColor: "inherit",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          py: 4,
        }}
      >
        <p
          sx={{
            fontFamily: "heading",
            color: fontColor,
            fontWeight: 400,
            fontSize: 4,
            lineHeight: "100%",
            backgroundColor: "inherit",
          }}
        >
          {introTitle}
        </p>
        {logos &&
          logos.map((singleLogo, index) => {
            const { logo, useFontColor } = singleLogo
            if (useFontColor) {
              return (
                <LogoColorable
                  name={logo}
                  color={fontColor}
                  sx={{ height: "41px", backgroundColor: "inherit" }}
                />
              )
            }
            return (
              <LogoColorable
                name={logo}
                color={logoColor}
                sx={{ height: "41px", backgroundColor: "inherit" }}
              />
            )
          })}
      </div>
    </SectionGrid>
  )
}

// mobile version of press logo component
function PressLogosMobile({ introTitle, backgroundColor, fontColor, pressLogos }) {
  const logoColor = lightOrDark(backgroundColor)
  return (
    <SectionGrid
      sx={{
        display: ["block", "block", "none"],
        pt: [0, 7],
        pb: [0, 10],
        backgroundColor: "inherit",
      }}
    >
      <div
        sx={{
          color: fontColor,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          backgroundColor: "inherit",
        }}
      >
        <p
          sx={{
            fontFamily: "heading",
            fontWeight: 500,
            fontSize: [1, 4],
            lineHeight: "136%",
            color: fontColor,
            mb: 7,
            backgroundColor: "inherit",
          }}
        >
          {introTitle}
        </p>
      </div>
      <div
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          backgroundColor: "inherit",
        }}
      >
        {pressLogos &&
          pressLogos.map((singleLogo, index) => {
            const { logo, useFontColor } = singleLogo
            if (useFontColor) {
              return (
                <LogoColorable
                  name={logo}
                  color={fontColor}
                  sx={{ height: "18px", backgroundColor: "inherit" }}
                />
              )
            }
            return (
              <LogoColorable
                name={logo}
                color={logoColor}
                sx={{ height: "18px", backgroundColor: "inherit" }}
              />
            )
          })}
      </div>
    </SectionGrid>
  )
}

// parental function
function PressLogos({ introTitle, backgroundColor, fontColor, pressLogos }) {
  // keep only logos to be shown on desktop/mobile devices
  const pressLogosDesktop = pressLogos.filter(singleLogo => singleLogo.showDesktop)
  const pressLogosMobile = pressLogos.filter(singleLogo => singleLogo.showMobile)
  return (
    <div sx={{ backgroundColor: backgroundColor, py: [7, 9] }}>
      <PressLogosDesktop
        introTitle={introTitle}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
        pressLogos={pressLogosDesktop}
      />
      <PressLogosMobile
        introTitle={introTitle}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
        pressLogos={pressLogosMobile}
      />
    </div>
  )
}

export { PressLogos }
